import { alpha, Box } from "@mui/material";
import { radius } from "../../../theme";
import { useEffect, useState } from "react";
import { DropEvent, ErrorCode, useDropzone } from "react-dropzone";
import { useSnackBar } from "../../../stores/snackBarStores";
import { IconUpload } from "@tabler/icons-react";
import { join, keys } from "lodash";

type DragEvent = {
  dragOn: boolean;
};

type Props = {
  onDropAccepted: <T extends File>(files: T[], event: DropEvent) => void;
  onDrag?: (event: DragEvent) => void;
  maxFiles?: number;
  acceptedFormats?: { pdf?: boolean; xlsx?: boolean };
};
const MAX_FILES = 100;
export default function Dropzone({
  onDropAccepted,
  onDrag = () => {},
  maxFiles = MAX_FILES,
  acceptedFormats = { pdf: true, xlsx: true },
}: Props) {
  const [dragOn, setDragOn] = useState(false);
  const snackbar = useSnackBar();
  useEffect(() => {
    onDrag({ dragOn });
  }, [dragOn]);
  let accept: { [key: string]: string[] } = {};
  if (acceptedFormats.pdf) {
    accept["application/pdf"] = [".pdf"];
  }
  if (acceptedFormats.xlsx) {
    accept[
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    ] = [".xlsx"];
  }
  const { getRootProps, getInputProps } = useDropzone({
    maxFiles,
    accept,
    onDrop: () => setDragOn(false),
    onDragEnter: () => setDragOn(true),
    onDragLeave: () => setDragOn(false),
    noKeyboard: true,
    onDropAccepted: onDropAccepted,
    onDropRejected: (fileRejection) => {
      let mensagem = "";
      switch (fileRejection[0].errors[0].code) {
        case ErrorCode.FileInvalidType:
          mensagem = `Apenas arquivos do tipo ${join(
            keys(acceptedFormats),
            " ou ",
          )} são aceitos.`;
          break;
        case ErrorCode.FileTooLarge:
          mensagem = `O arquivo é muito grande.`;
          break;
        case ErrorCode.FileTooSmall:
          mensagem = `O arquivo é muito pequeno.`;
          break;
        case ErrorCode.TooManyFiles:
          mensagem = `Muitos arquivos. Tente enviar ${maxFiles} arquivos por vez.`;
          break;
      }
      snackbar({
        type: "ALERT",
        payload: {
          severidade: "error",
          mensagem,
        },
      });
    },
  });

  return (
    <Box
      sx={(theme) => ({
        height: "100%",
        width: "100%",
        cursor: "pointer",
        padding: 2,
        // Área de soltar é SUPERFÍCIE, não bloco de cor: um retângulo grande em
        // accent sólido dominava a tela inteira. A afordância vem do traço
        // tracejado e do hover, como manda o DESIGN.md (hierarquia por hairline).
        borderRadius: `${radius.lg}px`,
        bgcolor: dragOn
          ? alpha(theme.palette.primary.main, 0.08)
          : "transparent",
        transition: theme.transitions.create("background-color", {
          duration: 120,
          easing: "ease-out",
        }),
        "&:hover": { bgcolor: theme.palette.action.hover },
      })}
      flexGrow={1}
      {...getRootProps()}
    >
      <Box
        sx={(theme) => ({
          height: "100%",
          width: "100%",
          padding: theme.spacing(4),
          // Traço e tinta em accent-ink: passa em contraste nos dois temas
          // (o accent sólido reprova sobre superfície escura).
          borderColor: dragOn
            ? theme.palette.accentInk
            : alpha(theme.palette.accentInk, 0.45),
          color: theme.palette.text.secondary,
          borderWidth: 2,
          borderStyle: dragOn ? "solid" : "dashed",
          borderRadius: `${radius.md}px`,
          transition: theme.transitions.create("background-color"),
          ":hover": {
            borderStyle: "solid",
          },
          "@keyframes dropzone": {
            from: { borderColor: alpha(theme.palette.accentInk, 0.45) },
            to: { borderColor: theme.palette.accentInk },
          },
          animation: dragOn
            ? `dropzone 600ms ${theme.transitions.easing.easeIn} infinite alternate both`
            : "",
        })}
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <input {...getInputProps()} />
        <Box
          sx={(theme) => ({
            "@keyframes icon": {
              from: {
                transform: "scale(1)",
              },
              to: {
                transform: "scale(1.2)",
              },
            },
            animation: dragOn
              ? `icon 600ms ${theme.transitions.easing.easeIn} infinite alternate both`
              : "",
          })}
        >
          <IconUpload size={40} />
        </Box>
      </Box>
    </Box>
  );
}
